import { useEffect, useRef, useState } from "react";
import { t, type Language } from "../i18n";
import { exportDebugEntries, type PriceTagDebugEntry } from "../scanner/priceTag/debug";

interface Props {
  language: Language;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  entries: PriceTagDebugEntry[];
  frame: { id: string; dataUrl: string } | null;
  onClear: () => void;
  busy: boolean;
  storageFailed: boolean;
}

export function PriceTagDebugPanel({ language, enabled, onToggle, entries, frame, onClear, busy, storageFailed }: Props) {
  const [open, setOpen] = useState(false);
  const [exportFallback, setExportFallback] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (open) dialogRef.current?.showModal();
    else dialogRef.current?.close();
  }, [open]);

  const exportLog = async () => {
    const json = exportDebugEntries(entries);
    const filename = `fit-price-tag-debug-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
    try {
      const file = new File([json], filename, { type: "application/json" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file] });
      } else {
        const url = URL.createObjectURL(file);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        // Some WebViews silently ignore downloads: always offer selectable JSON.
        setExportFallback(json);
      }
    } catch (error) {
      if (!(error instanceof Error && error.name === "AbortError")) setExportFallback(json);
    }
  };

  return (
    <>
      <button type="button" className="manual-button" onClick={() => setOpen(true)} data-testid="price-tag-debug-open">
        {t(language, "debug_title")} {enabled ? "●" : "○"} ({entries.length})
      </button>
      <dialog ref={dialogRef} className="price-tag-debug" aria-label={t(language, "debug_title")} onCancel={() => setOpen(false)}>
        <div className="price-tag-debug__header">
          <strong>{t(language, "debug_title")}</strong>
          <button type="button" autoFocus onClick={() => setOpen(false)}>{t(language, "debug_close")}</button>
        </div>
        <label>
          <input type="checkbox" checked={enabled} disabled={busy} onChange={(event) => onToggle(event.target.checked)} />
          {t(language, "debug_enable")}
        </label>
        <p>{t(language, "debug_hint")}</p>
        {storageFailed && <p role="status">{t(language, "debug_storage_failed")}</p>}
        <div className="price-tag-debug__actions">
          <button type="button" disabled={!entries.length} onClick={() => void exportLog()}>{t(language, "debug_export")}</button>
          <button type="button" disabled={!entries.length} onClick={() => setExportFallback(exportDebugEntries(entries))}>{t(language, "debug_show_json")}</button>
          <button type="button" disabled={busy || !entries.length} onClick={() => { onClear(); setExportFallback(null); }}>{t(language, "debug_clear")}</button>
        </div>
        {exportFallback && <label>{t(language, "debug_copy_hint")}
          <textarea readOnly value={exportFallback} onFocus={(event) => event.target.select()} />
        </label>}
        {entries.length === 0 && <p>{t(language, "debug_empty")}</p>}
        {entries.map((entry, index) => (
          <details key={entry.id} open={index === 0}>
            <summary>{new Date(entry.timestamp).toLocaleTimeString()} · {entry.storeId ?? "—"} · {entry.events[entry.events.length - 1]?.stage ?? "capture"} / {entry.events[entry.events.length - 1]?.status ?? "started"}</summary>
            <p>{t(language, entry.events.length > 0 && (entry.syncedRevision ?? 0) >= entry.events.length ? "debug_synced" : "debug_pending")}</p>
            {entry.uploadError && <p role="status">{entry.uploadError}</p>}
            {frame?.id === entry.id && <figure>
              <img src={frame.dataUrl} alt={t(language, "debug_frame")} />
              <figcaption>{t(language, "debug_frame")}</figcaption>
            </figure>}
            {entry.events.map((event, eventIndex) => (
              <section key={eventIndex}>
                <strong>{event.stage} · {event.status} · {Math.round(event.elapsedMs)} ms</strong>
                {event.stage === "ocr" && event.status === "success" && (
                  <pre className="price-tag-debug__text">{(event.data as { text?: string })?.text || t(language, "debug_no_text")}</pre>
                )}
                {(event.data !== undefined || event.error) && <pre>{JSON.stringify(event.error ?? event.data, null, 2)}</pre>}
              </section>
            ))}
          </details>
        ))}
      </dialog>
    </>
  );
}
